import { bayer } from "../shared/color";
import type { Pix } from "./rotsprite";

/** One sprite pixel is two CSS pixels, matching the original stickman scale. */
export const SCALE = 2;

export const BONE = { headR: 7.5, neck: 2, torso: 18, uarm: 12, farm: 12, thigh: 14, shin: 14 };
const LEG = BONE.thigh + BONE.shin;
const HIP = LEG * 0.95;

export interface Pt {
  x: number;
  y: number;
}

export interface RigInput {
  dt: number;
  t: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  grounded: boolean;
  facing: number;
  aim: number;
  holdDist: number;
  holdDrop: number;
  twoHanded: boolean;
  kick: number;
  kickRot: number;
  foregrip: (x: number, y: number, angle: number) => Pt;
  ground: (x: number, y: number) => number;
  flip: number;
  tumble: number;
  throwT: number;
  /** Seconds since a staff cast began (Meteor), or -1. */
  cast?: number;
  /** Seconds since the cast's meteor landed: staff-recover pose, or -1. */
  recover?: number;
  jet: number;
  sit?: boolean;
}

const GAIT_FAST: Array<[number, number, number]> = [
  [0, -1, 0],
  [0.16, -1.22, 6.5],
  [0.36, -0.85, 14.5],
  [0.58, 0.3, 12.5],
  [0.8, 1.08, 5.5],
  [1, 1, 0],
];
const GAIT_SLOW: Array<[number, number, number]> = [
  [0, -1, 0],
  [0.35, -0.35, 3.8],
  [0.65, 0.4, 3.6],
  [1, 1, 0],
];

function clamp(n: number, a: number, b: number) {
  return n < a ? a : n > b ? b : n;
}
function mix(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function angLerp(a: number, b: number, t: number) {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/**
 * Staff cast: raise the staff up and back, swing it through the aim with a
 * short overshoot, then settle. Recover: lift the staff upright as the meteor
 * lands, then lower it. Returns the held angle and an extra body lean.
 */
export function castPose(aim: number, facing: number, cast = -1, recover = -1): { aim: number; lean: number; reach: number } {
  let out = aim;
  let lean = 0;
  let reach = 0;
  const up = -Math.PI / 2 - facing * 0.45;
  if (cast >= 0 && cast < 0.46) {
    const t = cast;
    const over = aim + facing * 0.32;
    if (t < 0.05) {
      const u = smoothstep(t / 0.05);
      out = angLerp(aim, up, u);
      lean = -0.14 * u;
      reach = -3 * u;
    } else if (t < 0.13) {
      const u = smoothstep((t - 0.05) / 0.08);
      out = angLerp(up, over, u);
      lean = -0.14 + 0.36 * u;
      reach = -3 + 7 * u;
    } else {
      const u = smoothstep((t - 0.13) / 0.33);
      out = angLerp(over, aim, u);
      lean = 0.22 * (1 - u);
      reach = 4 * (1 - u);
    }
  }
  if (recover >= 0 && recover < 0.5) {
    const lift = -Math.PI / 2 + facing * 0.25;
    const k = recover < 0.08 ? smoothstep(recover / 0.08) : 1 - smoothstep((recover - 0.08) / 0.42);
    out = angLerp(out, lift, k * 0.85);
    lean += -0.1 * k;
    reach += 2 * k;
  }
  return { aim: out, lean, reach };
}

function smoothstep(t: number) {
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return t * t * (3 - 2 * t);
}
function smooth(t: number) {
  return t * t * (3 - 2 * t);
}
function frac(t: number) {
  return t - Math.floor(t);
}
function easeOut(t: number) {
  return 1 - (1 - t) ** 3;
}

function sampleGait(curve: Array<[number, number, number]>, t: number): [number, number] {
  let i = 0;
  while (i < curve.length - 2 && t > curve[i + 1][0]) i++;
  const a = curve[i];
  const b = curve[i + 1];
  const prev = curve[Math.max(0, i - 1)];
  const next = curve[Math.min(curve.length - 1, i + 2)];
  const u = clamp((t - a[0]) / (b[0] - a[0] || 1), 0, 1);
  const cat = (p0: number, p1: number, p2: number, p3: number) =>
    0.5 * (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u * u + (-p0 + 3 * p1 - 3 * p2 + p3) * u * u * u);
  return [cat(prev[1], a[1], b[1], next[1]), cat(prev[2], a[2], b[2], next[2])];
}

/** Two-bone IK. `bend` is the sign of the knee or elbow. Returns elbow and the clamped target. */
function ik(ax: number, ay: number, bx: number, by: number, lenA: number, lenB: number, bend: number): [number, number, number, number] {
  let dx = bx - ax;
  let dy = by - ay;
  let dist = Math.hypot(dx, dy);
  const max = lenA + lenB - 0.01;
  if (dist > max) {
    bx = ax + (dx / dist) * max;
    by = ay + (dy / dist) * max;
    dx = bx - ax;
    dy = by - ay;
    dist = max;
  }
  dist = Math.max(dist, Math.abs(lenA - lenB) + 0.5);
  const along = (lenA * lenA - lenB * lenB + dist * dist) / (2 * dist);
  const side = Math.sqrt(Math.max(0, lenA * lenA - along * along));
  const ux = dx / dist;
  const uy = dy / dist;
  return [ax + ux * along - uy * side * bend, ay + uy * along + ux * side * bend, bx, by];
}

class Spring {
  v = 0;
  x = 0;
  constructor(private readonly k: number, private readonly c: number) {}
  step(target: number, dt: number) {
    this.v += (-(this.x - target) * this.k - this.v * this.c) * dt;
    this.x += this.v * dt;
    return this.x;
  }
}

function pt(): Pt {
  return { x: 0, y: 0 };
}

/** Pixel buffer in ABGR, shared with an ImageData view. */
export class PixBuf {
  readonly bytes: Uint8ClampedArray;
  readonly data: Uint32Array;
  ox = 0;
  oy = 0;
  constructor(readonly w: number, readonly h: number) {
    this.bytes = new Uint8ClampedArray(w * h * 4);
    this.data = new Uint32Array(this.bytes.buffer);
  }
  clear(value = 0) {
    this.data.fill(value);
  }
  set(x: number, y: number, color: number) {
    x = (x - this.ox) | 0;
    y = (y - this.oy) | 0;
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    this.data[y * this.w + x] = color >>> 0;
  }
  add(x: number, y: number, color: number, amount: number) {
    x = (x - this.ox) | 0;
    y = (y - this.oy) | 0;
    if (x < 0 || y < 0 || x >= this.w || y >= this.h || amount <= 0) return;
    const i = y * this.w + x;
    const cur = this.data[i] >>> 0;
    const r = Math.min(255, (cur & 255) + (color & 255) * amount);
    const g = Math.min(255, ((cur >>> 8) & 255) + ((color >>> 8) & 255) * amount);
    const b = Math.min(255, ((cur >>> 16) & 255) + ((color >>> 16) & 255) * amount);
    // The original light buffer is always opaque. "lighter" then adds the whole colour.
    this.data[i] = (255 << 24) | ((b & 255) << 16) | ((g & 255) << 8) | (r & 255);
  }
  glow(x: number, y: number, radius: number, color: number, amount: number) {
    if (radius < 1 || amount <= 0.01) return;
    const x0 = Math.max(this.ox, Math.floor(x - radius));
    const x1 = Math.min(this.ox + this.w - 1, Math.ceil(x + radius));
    const y0 = Math.max(this.oy, Math.floor(y - radius));
    const y1 = Math.min(this.oy + this.h - 1, Math.ceil(y + radius));
    const inv = 1 / radius;
    for (let py = y0; py <= y1; py++) {
      const dy = (py + 0.5 - y) * inv;
      for (let px = x0; px <= x1; px++) {
        const dx = (px + 0.5 - x) * inv;
        const d2 = dx * dx + dy * dy;
        if (d2 >= 1) continue;
        const fall = 1 - Math.sqrt(d2);
        this.add(px, py, color, fall * fall * amount);
      }
    }
  }
  disc(x: number, y: number, radius: number, color: number) {
    if (radius <= 0.5) {
      this.set(Math.round(x), Math.round(y), color);
      return;
    }
    const r2 = radius * radius;
    const x0 = Math.floor(x - radius);
    const x1 = Math.ceil(x + radius);
    const y0 = Math.floor(y - radius);
    const y1 = Math.ceil(y + radius);
    for (let py = y0; py <= y1; py++) {
      const dy = py + 0.5 - y;
      for (let px = x0; px <= x1; px++) {
        const dx = px + 0.5 - x;
        if (dx * dx + dy * dy <= r2) this.set(px, py, color);
      }
    }
  }
  ring(x: number, y: number, radius: number, thick: number, color: number, alpha = 1) {
    const outer = radius + thick * 0.5;
    const inner = Math.max(0, radius - thick * 0.5);
    const o2 = outer * outer;
    const i2 = inner * inner;
    const x0 = Math.floor(x - outer);
    const x1 = Math.ceil(x + outer);
    const y0 = Math.floor(y - outer);
    const y1 = Math.ceil(y + outer);
    for (let py = y0; py <= y1; py++) {
      const dy = py + 0.5 - y;
      for (let px = x0; px <= x1; px++) {
        const dx = px + 0.5 - x;
        const d2 = dx * dx + dy * dy;
        if (d2 <= o2 && d2 >= i2 && (alpha >= 1 || alpha > bayer4(px, py))) this.set(px, py, color);
      }
    }
  }
  line(x0: number, y0: number, x1: number, y1: number, color: number) {
    let x = Math.round(x0);
    let y = Math.round(y0);
    const tx = Math.round(x1);
    const ty = Math.round(y1);
    const dx = Math.abs(tx - x);
    const dy = -Math.abs(ty - y);
    const sx = x < tx ? 1 : -1;
    const sy = y < ty ? 1 : -1;
    let err = dx + dy;
    for (let n = 0; n < 4096; n++) {
      this.set(x, y, color);
      if (x === tx && y === ty) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y += sy;
      }
    }
  }
  alphaDisc(x: number, y: number, radius: number, color: number, alpha: number) {
    if (alpha <= 0.01 || radius <= 0) return;
    const r2 = radius * radius;
    const x0 = Math.floor(x - radius);
    const x1 = Math.ceil(x + radius);
    const y0 = Math.floor(y - radius);
    const y1 = Math.ceil(y + radius);
    for (let py = y0; py <= y1; py++) {
      const dy = py + 0.5 - y;
      for (let px = x0; px <= x1; px++) {
        const dx = px + 0.5 - x;
        if (dx * dx + dy * dy <= r2) this.blend(px, py, color, alpha);
      }
    }
  }
  blend(x: number, y: number, color: number, amount: number) {
    x = (x - this.ox) | 0;
    y = (y - this.oy) | 0;
    if (x < 0 || y < 0 || x >= this.w || y >= this.h || amount <= 0) return;
    const i = y * this.w + x;
    const cur = this.data[i] >>> 0;
    const u = 1 - amount;
    const r = (cur & 255) * u + (color & 255) * amount;
    const g = ((cur >>> 8) & 255) * u + ((color >>> 8) & 255) * amount;
    const b = ((cur >>> 16) & 255) * u + ((color >>> 16) & 255) * amount;
    const a = Math.max((cur >>> 24) & 255, 255 * amount);
    this.data[i] = ((a & 255) << 24) | ((b & 255) << 16) | ((g & 255) << 8) | (r & 255);
  }
}

const BAYER4 = new Uint8Array([0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5]);

function bayer4(x: number, y: number) {
  return (BAYER4[((y & 3) << 2) | (x & 3)] + 0.5) / 16;
}

export interface HeadFrame {
  hx: number;
  hy: number;
  f: number;
  ux: number;
  uy: number;
  fx: number;
  fy: number;
  at: (across: number, up: number) => [number, number];
}

export interface TorsoFrame {
  ux: number;
  uy: number;
  nx: number;
  ny: number;
  len: number;
}

export function headFrame(rig: Rig): HeadFrame {
  const facing = rig.now.facing;
  const hx = rig.head.x / SCALE;
  const hy = rig.head.y / SCALE;
  let ux = rig.head.x - rig.chest.x;
  let uy = rig.head.y - rig.chest.y;
  const len = Math.hypot(ux, uy) || 1;
  ux /= len;
  uy /= len;
  const fx = -uy * facing;
  const fy = ux * facing;
  return {
    hx, hy, f: facing, ux, uy, fx, fy,
    at: (across, up) => [hx + fx * across + ux * up, hy + fy * across + uy * up],
  };
}

export function torsoFrame(rig: Rig): TorsoFrame {
  const facing = rig.now.facing;
  let ux = rig.chest.x - rig.pelvis.x;
  let uy = rig.chest.y - rig.pelvis.y;
  const len = Math.hypot(ux, uy) || 1;
  ux /= len;
  uy /= len;
  return { ux, uy, nx: -uy * facing, ny: ux * facing, len: len / SCALE };
}

export function shoulderPoint(rig: Rig, across: number, up: number): Pt {
  const torso = torsoFrame(rig);
  return {
    x: rig.shoulder.x + (-torso.nx * across + torso.ux * up) * SCALE,
    y: rig.shoulder.y + (-torso.ny * across + torso.uy * up) * SCALE,
  };
}

/** Push cloth points off the torso. Distances are CSS pixels. */
export function bodyPush(rig: Rig, x: number, y: number, vx: number, vy: number, pad: number, spring: number, damp: number): [number, number] {
  const torso = torsoFrame(rig);
  const facing = rig.now.facing;
  const dx = x - rig.pelvis.x;
  const dy = y - rig.pelvis.y;
  const along = dx * torso.ux + dy * torso.uy;
  if (along > (torso.len + 2) * SCALE || along < -(BONE.thigh + BONE.shin)) return [0, 0];
  let nx = torso.nx;
  let ny = torso.ny;
  if (along < 0) {
    const fx = (rig.footN.x + rig.footF.x) / 2 - rig.pelvis.x;
    const fy = (rig.footN.y + rig.footF.y) / 2 - rig.pelvis.y;
    const fl = Math.hypot(fx, fy) || 1;
    nx = (fy / fl) * facing;
    ny = (-fx / fl) * facing;
  }
  const side = dx * nx + dy * ny + pad;
  if (side <= 0) return [0, 0];
  const into = vx * nx + vy * ny;
  const push = Math.max(0, side * spring + into * 2 * Math.sqrt(spring));
  return [-nx * push - (vx - nx * into) * damp, -ny * push - (vy - ny * into) * damp];
}

export function flutter(rig: Rig, time: number, row: number, phase: number, freq: number, amp: number, speed: number): [number, number] {
  let x = -rig.now.vx;
  let y = -rig.now.vy + 60;
  const len = Math.hypot(x, y) || 1;
  const wave = Math.sin(time * freq - row * 1.3 + phase) * (amp + Math.hypot(rig.now.vx, rig.now.vy) * speed);
  return [-(y / len) * wave, (x / len) * wave];
}

export interface Pen {
  rig: Rig;
  data: Record<string, unknown>;
  near: number;
  far: number;
  dt: number;
  t: number;
  rand: () => number;
  cap: (a: Pt, b: Pt, radius: number, color: number) => void;
  disc: (x: number, y: number, radius: number, color: number) => void;
  px: (x: number, y: number, color: number, mask?: number) => void;
  glow: (x: number, y: number, radius: number, color: number, amount: number) => void;
  emit: (kind: string, x: number, y: number, vx: number, vy: number, life: number, color: number) => void;
}

export interface SkinDraw {
  id: string;
  near?: number;
  far?: number;
  outline?: number;
  /**
   * Set only when `head` paints a complete replacement head. A hat, lamp, or
   * nose keeps the base head disc. Magnus sets this if its head disc should
   * be the only one.
   */
  fullHead?: boolean;
  hide?: { torso?: boolean; arms?: boolean; legs?: boolean };
  step?: (pen: Pen) => void;
  back?: (pen: Pen) => void;
  body?: (pen: Pen) => void;
  head?: (pen: Pen) => void;
  front?: (pen: Pen) => void;
  glow?: (pen: Pen) => void;
}

export interface GunBlit {
  sprite: Pix;
  ox: number;
  oy: number;
  fade?: number;
}

/**
 * Procedural stickman. Joints are CSS pixels. Skins paint into a 128² sprite-pixel
 * buffer, which is the same rig the original uses for every wearable character.
 */
export class Rig {
  pelvis = pt();
  chest = pt();
  spine = pt();
  head = pt();
  shoulder = pt();
  elbowN = pt();
  handN = pt();
  elbowF = pt();
  handF = pt();
  kneeN = pt();
  kneeF = pt();
  footN = pt();
  footF = pt();
  grip = pt();
  gunAngle = 0;
  phase = 0;
  run = 0;
  mode: "air" | "gait" | "idle" | "sit" = "air";
  from = [pt(), pt()];
  blend = 1;
  feet = [pt(), pt()];
  planted = [pt(), pt()];
  stepT = [-1, -1];
  stepFrom = [pt(), pt()];
  stepTo = [pt(), pt()];
  dip = new Spring(420, 30);
  lean = new Spring(520, 38);
  headX = new Spring(420, 26);
  headY = new Spring(460, 28);
  prevChest = pt();
  prevPelvisY = 0;
  chestLag = new Spring(520, 30);
  gunLag = new Spring(480, 28);
  bend = new Spring(480, 36);
  sway = 0;
  prevVx = 0;
  accel = 0;
  airT = 0;
  fallT = 0;
  lastFacing = 1;
  now = { t: 0, vx: 0, vy: 0, facing: 1, grounded: true };
  worn: Record<string, Record<string, unknown>> = {};
  readonly local = new PixBuf(128, 128);
  readonly glowBuf = new PixBuf(128, 128);
  readonly mask = new Uint8Array(128 * 128);
  seen = Number.NaN;

  dataOf(id: string) {
    return (this.worn[id] ??= {});
  }

  update(input: RigInput) {
    if (input.t === this.seen) return;
    this.seen = input.t;
    const dt = input.dt;
    const facing = input.facing;
    const now = this.now;
    now.t = input.t;
    now.vx = input.vx;
    now.vy = input.vy;
    now.facing = facing;
    now.grounded = input.grounded;
    const speed = Math.abs(input.vx);
    const forward = input.vx * facing >= 0 ? 1 : -1;
    const grounded = input.grounded;
    const targetRun = grounded ? clamp((speed - 20) / 110, 0, 1) : this.run;
    this.run += (targetRun - this.run) * Math.min(1, dt * 34);
    const run = this.run;
    this.accel += ((input.vx - this.prevVx) / Math.max(dt, 1e-4) - this.accel) * Math.min(1, dt * 22);
    this.prevVx = input.vx;
    let mode: "air" | "gait" | "idle" | "sit" = input.sit ? "sit" : !grounded ? "air" : speed > 26 ? "gait" : "idle";
    if (grounded) this.airT = 0;
    else this.airT += dt;
    this.fallT = !grounded && input.vy > 380 ? Math.min(1, this.fallT + dt * 2.2) : Math.max(0, this.fallT - dt * 6);
    if (mode === "air" && this.airT < 0.05 && this.mode !== "air") mode = this.mode;
    this.lastFacing = facing;
    if (mode !== this.mode) {
      this.from[0] = { ...this.feet[0] };
      this.from[1] = { ...this.feet[1] };
      this.blend = 0;
      if (mode === "gait") {
        const lead = (this.feet[0].x - input.x) * facing > (this.feet[1].x - input.x) * facing;
        const duty = mix(0.55, 0.36, run);
        this.phase = (lead ? duty * 0.2 : duty * 0.2 + 0.5) * (forward > 0 ? 1 : -1);
      }
      if (mode === "idle") {
        const home = [facing * 6.5, -facing * 5];
        for (let i = 0; i < 2; i++) {
          const x = input.x + home[i];
          let y = input.ground(x, input.y);
          if (Number.isNaN(y)) y = input.y;
          this.planted[i] = { x, y };
          this.stepT[i] = -1;
        }
      }
      this.mode = mode;
    }
    this.blend = Math.min(1, this.blend + dt / (mode === "air" ? 0.08 : 0.055));
    const duty = mix(0.55, 0.36, run);
    const stride = mix(15, 40, run) * (forward < 0 ? 0.72 : 1);
    const stepLen = stride * 2;
    const span = duty * stride;
    const goal = [pt(), pt()];
    let hipDrop = HIP;
    let hipX = input.x;
    const idleWave = Math.sin(input.t * 2.3);
    if (mode === "sit") {
      hipDrop = 0.4 - idleWave * 0.35;
      hipX = input.x;
      const swing = Math.sin(input.t * 1.7);
      goal[0] = { x: input.x + facing * 11, y: input.y + 7 };
      goal[1] = { x: input.x + facing * 5, y: input.y + 17 + swing };
    } else if (mode === "gait") {
      this.phase += (forward * speed * dt) / stepLen;
      for (let i = 0; i < 2; i++) {
        const g = frac(this.phase + i * 0.5);
        let along: number;
        let lift: number;
        if (g < duty) {
          along = span * (1 - (2 * g) / duty);
          lift = 0;
        } else {
          const u = (g - duty) / (1 - duty);
          const fast = sampleGait(GAIT_FAST, u);
          const slow = sampleGait(GAIT_SLOW, u);
          along = mix(slow[0], fast[0], run) * span;
          lift = mix(slow[1], fast[1], run);
        }
        const x = input.x + along * facing;
        let y = input.ground(x, input.y);
        if (Number.isNaN(y)) y = input.y;
        goal[i] = { x, y: y - lift };
      }
      const bob = mix(1.3, 3.6, run);
      const beat = frac(2 * this.phase - duty * 0.55);
      const plant = Math.pow(0.5 + 0.5 * Math.cos(2 * Math.PI * beat), mix(1, 2.2, run));
      hipDrop = mix(HIP - 0.6, HIP - 3.6, run) - bob * plant;
      hipX = input.x + facing * forward * (1.6 * run + Math.sin(2 * Math.PI * beat + 0.6) * 1.4 * run);
    } else if (mode === "idle") {
      const home = [facing * 6.5, -facing * 5];
      for (let i = 0; i < 2; i++) {
        const x = input.x + home[i];
        const planted = this.planted[i];
        if (this.stepT[i] < 0) {
          const other = this.stepT[1 - i];
          if (Math.abs(planted.x - x) > 5 && other < 0) {
            this.stepT[i] = 0;
            this.stepFrom[i] = { ...planted };
            let y = input.ground(x, input.y);
            if (Number.isNaN(y)) y = input.y;
            this.stepTo[i] = { x, y };
          }
        }
        if (this.stepT[i] >= 0) {
          this.stepT[i] = Math.min(1, this.stepT[i] + dt / 0.08);
          const u = smooth(this.stepT[i]);
          const from = this.stepFrom[i];
          const to = this.stepTo[i];
          planted.x = mix(from.x, to.x, u);
          planted.y = mix(from.y, to.y, u) - Math.sin(Math.PI * this.stepT[i]) * 4;
          if (this.stepT[i] >= 1) this.stepT[i] = -1;
        } else {
          const y = input.ground(planted.x, input.y);
          planted.y = Number.isNaN(y) ? input.y : y;
        }
        goal[i] = { x: planted.x, y: planted.y };
      }
      hipDrop = HIP - 0.8 + idleWave * 0.6;
      hipX = input.x + Math.sin(input.t * 0.9) * 0.9;
    } else {
      const rise = clamp(input.vy / 650, -1, 1);
      const up = clamp(-rise, 0, 1);
      const down = clamp(rise, 0, 1);
      const tuck = input.flip >= 0 ? Math.sin(Math.PI * clamp(input.flip * 1.15, 0, 1)) : 0;
      const hip = input.y - HIP;
      const lead = { x: 6 - 1 * down, y: mix(mix(19, 11, up), 24, down) };
      const trail = { x: -5 - 2 * up, y: mix(mix(22, 18, up), 26.5, down) };
      lead.x = mix(lead.x, 8, tuck);
      lead.y = mix(lead.y, 8, tuck);
      trail.x = mix(trail.x, 3, tuck);
      trail.y = mix(trail.y, 11, tuck);
      const swing = Math.sin(input.t * 9) * 2.5 * clamp(speed / 250, 0, 1) * (1 - tuck);
      goal[0] = { x: input.x + facing * (lead.x + swing), y: hip + lead.y };
      goal[1] = { x: input.x + facing * (trail.x - swing), y: hip + trail.y };
      const fall = smooth(this.fallT) * (1 - tuck);
      if (fall > 0) {
        const wobble = input.t * 11;
        goal[0].x = mix(goal[0].x, input.x + facing * (6 + Math.sin(wobble) * 5), fall);
        goal[0].y = mix(goal[0].y, hip + 19 + Math.cos(wobble) * 4, fall);
        goal[1].x = mix(goal[1].x, input.x + facing * (-6 + Math.sin(wobble + Math.PI) * 5), fall);
        goal[1].y = mix(goal[1].y, hip + 21 + Math.cos(wobble + Math.PI) * 4, fall);
      }
      const jet = smooth(clamp(input.jet * 1.4, 0, 1)) * (1 - tuck);
      if (jet > 0) {
        const drift = clamp((-input.vx * facing) / 400, -1, 1);
        const flutterY = Math.sin(input.t * 6) * 1.2;
        goal[0].x = mix(goal[0].x, input.x + facing * (1.5 + drift * 5 + flutterY), jet);
        goal[0].y = mix(goal[0].y, hip + 25 - Math.abs(drift) * 2, jet);
        goal[1].x = mix(goal[1].x, input.x + facing * (-2.5 + drift * 6 - flutterY), jet);
        goal[1].y = mix(goal[1].y, hip + 23.5 - Math.abs(drift) * 2, jet);
      }
      hipDrop = HIP;
    }
    const blend = smooth(this.blend);
    for (let i = 0; i < 2; i++) {
      this.feet[i] = { x: mix(this.from[i].x, goal[i].x, blend), y: mix(this.from[i].y, goal[i].y, blend) };
    }
    const dip = this.dip.step(0, dt);
    let pelvisY = input.y - hipDrop + Math.max(-3, dip);
    if (mode !== "air" && mode !== "sit") {
      for (const foot of this.feet) {
        const dx = foot.x - hipX;
        const dy = foot.y - pelvisY;
        if (Math.hypot(dx, dy) > LEG * 0.985 && dy > 0) pelvisY = foot.y - Math.sqrt(Math.max(0, (LEG * 0.985) ** 2 - dx * dx));
      }
    }
    this.pelvis = { x: hipX, y: pelvisY };
    let leanTarget: number;
    if (mode === "sit") leanTarget = -0.2;
    else if (mode === "air") {
      leanTarget = clamp((input.vx * facing) / 900, -0.18, 0.28) + (input.vy < 0 ? -0.04 : 0.06);
      if (input.jet > 0) leanTarget = mix(leanTarget, clamp((input.vx * facing) / 700, -0.35, 0.4), clamp(input.jet * 1.5, 0, 1));
    } else {
      leanTarget = 0.05 + forward * 0.24 * run - clamp((this.accel * facing) / 14000, -0.18, 0.16) * (mode === "gait" ? 1 : 0.3);
      if (mode === "gait") leanTarget += Math.sin(4 * Math.PI * this.phase + 0.9) * 0.045 * run * forward;
    }
    leanTarget -= input.kick * 0.012;
    const staff = castPose(input.aim, facing, input.cast ?? -1, input.recover ?? -1);
    leanTarget += staff.lean;
    const lean = this.lean.step(leanTarget, dt);
    const pelvisV = (this.pelvis.y - this.prevPelvisY) / Math.max(dt, 1e-4);
    this.prevPelvisY = this.pelvis.y;
    if (Math.abs(pelvisV) < 900) this.chestLag.v += -pelvisV * dt * 1.4;
    const lag = clamp(this.chestLag.step(0, dt), -2.2, 2.2);
    const bendTarget = mode === "sit" ? 0.14 : mode === "gait" ? 0.1 + 0.14 * run : mode === "air" ? (input.vy < 0 ? -0.06 : 0.08) : 0.05 + idleWave * 0.02;
    const bend = this.bend.step(bendTarget, dt);
    const half = BONE.torso * 0.5;
    const lower = lean - bend * 0.5;
    const upper = lean + bend;
    this.spine = {
      x: this.pelvis.x + facing * Math.sin(lower) * half,
      y: this.pelvis.y - Math.cos(lower) * half + lag * 0.4,
    };
    this.chest = {
      x: this.spine.x + facing * Math.sin(upper) * half,
      y: this.spine.y - Math.cos(upper) * half + lag + idleWave * 0.25 * (mode === "idle" || mode === "sit" ? 1 : 0),
    };
    const chestVx = (this.chest.x - this.prevChest.x) / Math.max(dt, 1e-4);
    const chestVy = (this.chest.y - this.prevChest.y) / Math.max(dt, 1e-4);
    this.prevChest = { ...this.chest };
    if (Math.abs(chestVx) < 900) this.headX.v += -chestVx * dt * 1.4;
    const hx = this.headX.step(0, dt);
    if (Math.abs(chestVy) < 900) this.headY.v += -chestVy * dt * 2.6;
    const hy = this.headY.step(0, dt);
    const neck = BONE.neck + BONE.headR;
    const headLean = lean * 0.7 + bend * 0.9;
    this.head = {
      x: this.chest.x + facing * Math.sin(headLean) * neck + Math.cos(input.aim) * 1.1 + clamp(hx, -2.5, 2.5),
      y: this.chest.y - Math.cos(headLean) * neck + Math.sin(input.aim) * 1.1 * 0.6 + clamp(hy, -2.2, 2.8),
    };
    this.shoulder = { x: mix(this.spine.x, this.chest.x, 0.62), y: mix(this.spine.y, this.chest.y, 0.62) };
    const aimX = Math.cos(staff.aim);
    const aimY = Math.sin(staff.aim);
    const acrossX = -aimY * facing;
    const acrossY = aimX * facing;
    if (Math.abs(chestVy) < 900) this.gunLag.v += -chestVy * dt * 1.1;
    const gunLag = clamp(this.gunLag.step(0, dt), -2.5, 2.5);
    const bob = mode === "gait" ? Math.sin(4 * Math.PI * this.phase + 1.2) * 0.045 * run : 0;
    this.sway += ((input.jet > 0 ? Math.sin(input.t * 5) * 0.03 : 0) - this.sway) * Math.min(1, dt * 6);
    const gripX = this.shoulder.x + aimX * (input.holdDist - input.kick + staff.reach) + acrossX * input.holdDrop;
    const gripY = this.shoulder.y + aimY * (input.holdDist - input.kick + staff.reach) + acrossY * input.holdDrop + gunLag;
    this.grip = { x: gripX, y: gripY };
    this.gunAngle = staff.aim - facing * input.kickRot + facing * (bob + this.sway);
    const freeHand = (sign: number): Pt => {
      if (mode === "air") {
        const rest = { x: this.shoulder.x - facing * 7, y: this.shoulder.y + 10 - clamp(-input.vy / 60, -4, 8) };
        const fall = smooth(this.fallT);
        if (fall <= 0) return rest;
        const wobble = input.t * 13;
        return {
          x: mix(rest.x, this.shoulder.x - facing * (6 + Math.sin(wobble) * 6), fall),
          y: mix(rest.y, this.shoulder.y - 16 + Math.cos(wobble * 0.8) * 5, fall),
        };
      }
      const swing = Math.sin(2 * Math.PI * this.phase) * sign;
      const idle = 1 - run;
      return {
        x: this.shoulder.x + facing * (1.5 + swing * mix(6, 11, run)),
        y: this.shoulder.y + mix(20, 15, run) - Math.max(0, swing) * 6 * run + Math.abs(swing) * idle * 0.8,
      };
    };
    let near = { x: gripX, y: gripY };
    let far = freeHand(-1);
    if (input.twoHanded) far = { x: gripX, y: gripY };
    else near = { x: gripX, y: gripY };
    if (input.twoHanded) near = input.foregrip(gripX, gripY, this.gunAngle);
    if (input.throwT >= 0) {
      const t = input.throwT;
      const wind = { x: this.shoulder.x - facing * 9, y: this.shoulder.y - 13 };
      const out = { x: this.shoulder.x + aimX * 20, y: this.shoulder.y + aimY * 20 - 4 };
      const follow = { x: this.shoulder.x + facing * 12, y: this.shoulder.y + 14 };
      const slide = (a: Pt, b: Pt, u: number) => ({ x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u });
      let thrown: Pt;
      if (t < 0.4) thrown = slide(input.twoHanded ? near : far, wind, smooth(t / 0.4));
      else if (t < 0.55) thrown = slide(wind, out, smooth((t - 0.4) / 0.15));
      else if (t < 0.8) thrown = slide(out, follow, smooth((t - 0.55) / 0.25));
      else thrown = slide(follow, input.twoHanded ? near : far, smooth((t - 0.8) / 0.2));
      if (input.twoHanded) near = thrown;
      else far = thrown;
    }
    const armBend = facing * (Math.cos(input.aim) * facing > -0.2 ? 1 : 1);
    let bone = ik(this.shoulder.x, this.shoulder.y, near.x, near.y, BONE.uarm, BONE.farm, armBend);
    this.elbowN = { x: bone[0], y: bone[1] };
    this.handN = { x: bone[2], y: bone[3] };
    bone = ik(this.shoulder.x, this.shoulder.y, far.x, far.y, BONE.uarm, BONE.farm, facing);
    this.elbowF = { x: bone[0], y: bone[1] };
    this.handF = { x: bone[2], y: bone[3] };
    bone = ik(this.pelvis.x, this.pelvis.y, this.feet[0].x, this.feet[0].y, BONE.thigh, BONE.shin, -facing);
    this.kneeN = { x: bone[0], y: bone[1] };
    this.footN = { x: bone[2], y: bone[3] };
    bone = ik(this.pelvis.x, this.pelvis.y, this.feet[1].x, this.feet[1].y, BONE.thigh, BONE.shin, -facing);
    this.kneeF = { x: bone[0], y: bone[1] };
    this.footF = { x: bone[2], y: bone[3] };
    let spin = input.tumble;
    if (input.flip >= 0) spin += facing * Math.PI * 2 * easeOut(clamp(input.flip, 0, 1));
    if (spin !== 0) {
      const ox = mix(this.pelvis.x, this.chest.x, 0.35);
      const oy = mix(this.pelvis.y, this.chest.y, 0.35);
      const c = Math.cos(spin);
      const s = Math.sin(spin);
      const rot = (p: Pt) => {
        const dx = p.x - ox;
        const dy = p.y - oy;
        p.x = ox + dx * c - dy * s;
        p.y = oy + dx * s + dy * c;
      };
      for (const p of [this.pelvis, this.spine, this.chest, this.head, this.shoulder, this.kneeN, this.kneeF, this.footN, this.footF]) rot(p);
      bone = ik(this.shoulder.x, this.shoulder.y, near.x, near.y, BONE.uarm, BONE.farm, armBend);
      this.elbowN = { x: bone[0], y: bone[1] };
      this.handN = { x: bone[2], y: bone[3] };
      bone = ik(this.shoulder.x, this.shoulder.y, far.x, far.y, BONE.uarm, BONE.farm, facing);
      this.elbowF = { x: bone[0], y: bone[1] };
      this.handF = { x: bone[2], y: bone[3] };
    }
  }

  cap(a: Pt, b: Pt, radius: number, color: number) {
    const buf = this.local;
    const mask = this.mask;
    const x0 = a.x / SCALE;
    const y0 = a.y / SCALE;
    const x1 = b.x / SCALE;
    const y1 = b.y / SCALE;
    const minX = Math.floor(Math.min(x0, x1) - radius);
    const maxX = Math.ceil(Math.max(x0, x1) + radius);
    const minY = Math.floor(Math.min(y0, y1) - radius);
    const maxY = Math.ceil(Math.max(y0, y1) + radius);
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len2 = dx * dx + dy * dy || 0.000001;
    const r2 = radius * radius;
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const px = x + 0.5;
        const py = y + 0.5;
        let t = ((px - x0) * dx + (py - y0) * dy) / len2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const ox = x0 + dx * t - px;
        const oy = y0 + dy * t - py;
        if (ox * ox + oy * oy > r2) continue;
        const lx = x - buf.ox;
        const ly = y - buf.oy;
        if (lx < 0 || ly < 0 || lx >= buf.w || ly >= buf.h) continue;
        buf.data[ly * buf.w + lx] = color >>> 0;
        mask[ly * buf.w + lx] = 1;
      }
    }
  }

  disc(x: number, y: number, radius: number, color: number) {
    const buf = this.local;
    const mask = this.mask;
    const r2 = radius * radius;
    for (let py = Math.floor(y - radius); py <= Math.ceil(y + radius); py++) {
      for (let px = Math.floor(x - radius); px <= Math.ceil(x + radius); px++) {
        const dx = px + 0.5 - x;
        const dy = py + 0.5 - y;
        if (dx * dx + dy * dy > r2) continue;
        const lx = px - buf.ox;
        const ly = py - buf.oy;
        if (lx < 0 || ly < 0 || lx >= buf.w || ly >= buf.h) continue;
        buf.data[ly * buf.w + lx] = color >>> 0;
        mask[ly * buf.w + lx] = 1;
      }
    }
  }

  private blit(sprite: Pix, x: number, y: number, fade = 1) {
    const buf = this.local;
    const mask = this.mask;
    for (let row = 0; row < sprite.h; row++) {
      for (let col = 0; col < sprite.w; col++) {
        const color = sprite.data[row * sprite.w + col];
        if (color >>> 24 < 128) continue;
        if (fade < 1 && bayer(x + col, y + row) > fade) continue;
        const lx = x + col - buf.ox;
        const ly = y + row - buf.oy;
        if (lx < 0 || ly < 0 || lx >= buf.w || ly >= buf.h) continue;
        buf.data[ly * buf.w + lx] = color >>> 0;
        mask[ly * buf.w + lx] = 2;
      }
    }
  }

  /**
   * Paint the rig. `near`/`far`/`outline` are ABGR. The gun sprite is already rotated;
   * its pivot sits on the grip.
   */
  paint(opts: {
    near: number;
    far: number;
    outline: number;
    shadow: number;
    twoHanded: boolean;
    grasp: boolean;
    gun: GunBlit | null;
    pack: GunBlit | null;
    wear: SkinDraw[];
    hideTorso: boolean;
    hideArms: boolean;
    hideLegs: boolean;
    hideHead: boolean;
    pen: Pen;
  }) {
    const buf = this.local;
    const mask = this.mask;
    const ox = Math.round(this.pelvis.x / SCALE) - 64;
    const oy = Math.round(this.pelvis.y / SCALE) - 64;
    buf.ox = ox;
    buf.oy = oy;
    this.glowBuf.ox = ox;
    this.glowBuf.oy = oy;
    buf.clear(0);
    this.glowBuf.clear(0);
    mask.fill(0);
    const call = (name: "back" | "body" | "head" | "front" | "glow") => {
      for (const skin of opts.wear) {
        const fn = skin[name];
        if (!fn) continue;
        opts.pen.data = this.dataOf(skin.id);
        fn.call(skin, opts.pen);
      }
    };
    const limb = (a: Pt, b: Pt, color: number) => this.cap(a, b, 0.95, color);
    if (opts.pack && opts.pack.fade !== 0) this.blit(opts.pack.sprite, Math.round(opts.pack.ox), Math.round(opts.pack.oy), opts.pack.fade ?? 1);
    call("back");
    if (!opts.hideArms) {
      limb(this.shoulder, this.elbowF, opts.far);
      limb(this.elbowF, this.handF, opts.far);
    }
    if (!opts.hideLegs) {
      limb(this.pelvis, this.kneeF, opts.far);
      limb(this.kneeF, this.footF, opts.far);
    }
    if (!opts.hideTorso) {
      limb(this.pelvis, this.spine, opts.near);
      limb(this.spine, this.chest, opts.near);
    }
    if (!opts.hideLegs) {
      limb(this.pelvis, this.kneeN, opts.near);
      limb(this.kneeN, this.footN, opts.near);
    }
    call("body");
    if (!opts.hideHead) this.disc(this.head.x / SCALE, this.head.y / SCALE, BONE.headR / SCALE, opts.near);
    call("head");
    const gun = () => {
      if (!opts.gun) return;
      const x = Math.round(this.grip.x / SCALE - 0.5) - opts.gun.ox;
      const y = Math.round(this.grip.y / SCALE - 0.5) - opts.gun.oy;
      this.blit(opts.gun.sprite, x, y, opts.gun.fade ?? 1);
    };
    if (opts.twoHanded) {
      gun();
      if (!opts.hideArms) {
        limb(this.shoulder, this.elbowN, opts.near);
        limb(this.elbowN, this.handN, opts.near);
      }
    } else {
      if (!opts.hideArms) {
        limb(this.shoulder, this.elbowN, opts.near);
        limb(this.elbowN, this.handN, opts.near);
      }
      gun();
      if (opts.grasp) this.disc(this.handN.x / SCALE, this.handN.y / SCALE, 1.8, opts.near);
    }
    call("front");
    for (let y = 1; y < buf.h - 1; y++) {
      for (let x = 1; x < buf.w - 1; x++) {
        const i = y * buf.w + x;
        if (mask[i]) continue;
        if (mask[i - 1] === 1 || mask[i + 1] === 1 || mask[i - buf.w] === 1 || mask[i + buf.w] === 1) {
          buf.data[i] = opts.outline >>> 0;
          mask[i] = 3;
        }
      }
    }
    call("glow");
    void opts.shadow;
  }
}
